import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouterSafe } from '../useRouterSafe';

// Type definitions for parameter configuration
export interface QueryParamConfig {
    name?: string; // Override URL parameter name
    type: 'string' | 'number' | 'boolean' | 'array';
    defaultValue?: unknown;
    parser?: (value: string) => unknown;
    serializer?: (value: unknown) => string;
    // When type is 'array', parser will be applied to each array element
    // Example: { type: 'array', parser: Number } converts ['1', '2'] to [1, 2]
    /** When set, empty arrays serialize to this token so URL round-trip preserves "explicitly cleared" vs param absent */
    emptyArrayUrlToken?: string;
}

export interface QueryParamConfigMap {
    [key: string]: QueryParamConfig;
}

export interface UseUrlQuerySyncOptions<T extends Record<string, unknown>> {
    config: QueryParamConfigMap;
    initialValues?: Partial<T>;
    onParamsChange?: (params: T) => void;
    debounceMs?: number;
}

// Built-in type parsers
const typeParsers = {
    string: (value: string) => value,
    number: (value: string) => {
        const num = Number(value);
        return Number.isNaN(num) ? undefined : num;
    },
    boolean: (value: string) => {
        if (value === 'true') return true;
        if (value === 'false') return false;
        return undefined;
    },
    array: (value: string) => {
        try {
            // Handle both comma-separated and JSON array formats
            if (value.startsWith('[') && value.endsWith(']')) {
                return JSON.parse(value);
            }
            return value.split(',').filter(Boolean);
        } catch {
            return value.split(',').filter(Boolean);
        }
    },
};

// Built-in type serializers
const typeSerializers = {
    string: (value: unknown) => String(value),
    number: (value: unknown) => String(value),
    boolean: (value: unknown) => String(value),
    array: (value: unknown) => {
        if (!Array.isArray(value)) return '';
        return value.length > 0 ? value.join(',') : '';
    },
};

// Helper function to parse URL parameters based on config
const parseUrlParams = <T extends Record<string, unknown>>(searchParams: URLSearchParams, config: QueryParamConfigMap, initialValues: Partial<T> = {}): T => {
    const result = { ...initialValues } as T;

    for (const [key, paramConfig] of Object.entries(config)) {
        const paramName = paramConfig.name || key;

        if (paramConfig.type === 'array') {
            // For arrays, handle both multiple params and comma-separated single param
            let values = searchParams.getAll(paramName).filter(Boolean);

            // Check if we have comma-separated values in any of the getAll results
            const flattenedValues: string[] = [];
            for (const value of values) {
                if (value.includes(',')) {
                    // Split comma-separated values
                    flattenedValues.push(...value.split(',').filter(Boolean));
                } else {
                    flattenedValues.push(value);
                }
            }

            values = flattenedValues;

            if (values.length > 0 && paramConfig.emptyArrayUrlToken && values.length === 1 && values[0] === paramConfig.emptyArrayUrlToken) {
                (result as Record<string, unknown>)[key] = [];
            } else if (values.length > 0) {
                // Apply parser to each array element if provided
                if (paramConfig.parser) {
                    const parsed = values.map(val => paramConfig.parser!(String(val)));
                    (result as Record<string, unknown>)[key] = parsed;
                } else {
                    (result as Record<string, unknown>)[key] = values;
                }
            } else if (paramConfig.defaultValue !== undefined) {
                (result as Record<string, unknown>)[key] = paramConfig.defaultValue;
            }
        } else {
            // For non-arrays, use standard parsing
            const rawValue = searchParams.get(paramName);

            if (rawValue !== null) {
                const parser = paramConfig.parser || typeParsers[paramConfig.type];
                const parsedValue = parser(rawValue);

                if (parsedValue !== undefined) {
                    (result as Record<string, unknown>)[key] = parsedValue;
                } else if (paramConfig.defaultValue !== undefined) {
                    (result as Record<string, unknown>)[key] = paramConfig.defaultValue;
                }
            } else if (paramConfig.defaultValue !== undefined) {
                (result as Record<string, unknown>)[key] = paramConfig.defaultValue;
            }
        }
    }

    return result;
};

// Helper function to serialize parameters to URL
const serializeToUrl = (params: Record<string, unknown>, config: QueryParamConfigMap, includeDefaults = false): URLSearchParams => {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
        const paramConfig = config[key];
        if (!paramConfig) continue;

        const paramName = paramConfig.name || key;

        // Skip null, undefined, and empty values
        if (value === null || value === undefined || value === '') {
            continue;
        }

        // Skip empty arrays unless a token preserves explicit empty in the URL
        if (Array.isArray(value) && value.length === 0) {
            if (paramConfig.emptyArrayUrlToken) {
                searchParams.set(paramName, paramConfig.emptyArrayUrlToken);
            }
            continue;
        }

        // Skip default values to keep URL clean (unless includeDefaults is true)
        if (!includeDefaults && paramConfig.defaultValue !== undefined && value === paramConfig.defaultValue) {
            continue;
        }

        const serializer = paramConfig.serializer || typeSerializers[paramConfig.type];
        const serializedValue = serializer(value);

        if (serializedValue !== '') {
            searchParams.set(paramName, serializedValue);
        }
    }

    return searchParams;
};

function collectConfigUrlKeys(config: QueryParamConfigMap): string[] {
    const keys: string[] = [];
    for (const [key, paramConfig] of Object.entries(config)) {
        keys.push(paramConfig.name || key);
    }
    return keys;
}

/**
 * Writes only this sync hook's query keys while preserving all other params (e.g. `tab`, sibling filters).
 */
function mergeSerializedParamsIntoCurrentSearch(
    current: URLSearchParams,
    newParams: Record<string, unknown>,
    config: QueryParamConfigMap,
    includeDefaults: boolean,
): URLSearchParams {
    const merged = new URLSearchParams(current);
    for (const paramName of collectConfigUrlKeys(config)) {
        merged.delete(paramName);
    }
    const fragment = serializeToUrl(newParams, config, includeDefaults);
    for (const [k, v] of fragment.entries()) {
        merged.set(k, v);
    }
    return merged;
}

/**
 * Hook for synchronizing UI state with URL query parameters
 *
 * @param options Configuration options
 * @returns Object with current params, update function, and reset function
 */
export const useUrlQuerySync = <T extends Record<string, any>>(options: UseUrlQuerySyncOptions<T>) => {
    const { config, initialValues = {}, onParamsChange, debounceMs = 300 } = options;

    const { searchParams, setSearchParams, isInRouterContext } = useRouterSafe();

    const [params, setParams] = useState<T>(() => (isInRouterContext ? parseUrlParams<T>(searchParams, config, initialValues) : ({} as T)));

    const memoizedParams = useMemo(() => params, [params]);

    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const isUpdatingFromUrl = useRef(false);
    const configRef = useRef(config);
    const initialValuesRef = useRef(initialValues);
    const onParamsChangeRef = useRef(onParamsChange);
    const searchParamsRef = useRef(searchParams);
    /** Bumps on every local params write so stale setTimeout URL updates are skipped. */
    const writeGenerationRef = useRef(0);

    configRef.current = config;
    initialValuesRef.current = initialValues;
    onParamsChangeRef.current = onParamsChange;
    searchParamsRef.current = searchParams;

    const updateUrl = useCallback(
        (newParams: T, replace = false, includeDefaults = false) => {
            if (!isInRouterContext || isUpdatingFromUrl.current) return;

            const merged = mergeSerializedParamsIntoCurrentSearch(searchParamsRef.current, newParams, configRef.current, includeDefaults);
            setSearchParams(merged, { replace });
        },
        [setSearchParams, isInRouterContext],
    );

    const debouncedUpdateUrl = useCallback(
        (newParams: T, replace = false, includeDefaults = false) => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }

            debounceTimer.current = setTimeout(() => {
                updateUrl(newParams, replace, includeDefaults);
            }, debounceMs) as NodeJS.Timeout;
        },
        [updateUrl, debounceMs],
    );

    const updateParams = useCallback(
        (newParams: Partial<T> | ((prev: T) => T), options: { replace?: boolean; immediate?: boolean } = {}) => {
            const { replace = false, immediate = false } = options;

            setParams(prev => {
                const updated = typeof newParams === 'function' ? newParams(prev) : { ...prev, ...newParams };
                const writeGeneration = ++writeGenerationRef.current;

                setTimeout(() => {
                    // Drop superseded writes (rapid pagination / filter churn).
                    if (writeGeneration !== writeGenerationRef.current) {
                        return;
                    }
                    if (immediate) {
                        if (debounceTimer.current) {
                            clearTimeout(debounceTimer.current);
                            debounceTimer.current = null;
                        }
                        updateUrl(updated, replace, true);
                    } else {
                        debouncedUpdateUrl(updated, replace, true);
                    }
                }, 0);

                onParamsChangeRef.current?.(updated);

                return updated;
            });
        },
        [updateUrl, debouncedUpdateUrl],
    );

    const resetParams = useCallback(
        (replace = false) => {
            const defaultParams = Object.entries(configRef.current).reduce((acc, [key, paramConfig]) => {
                if (paramConfig.defaultValue !== undefined) {
                    (acc as Record<string, unknown>)[key] = paramConfig.defaultValue;
                } else if (initialValuesRef.current && key in initialValuesRef.current) {
                    (acc as Record<string, unknown>)[key] = (initialValuesRef.current as Record<string, unknown>)[key];
                }
                return acc;
            }, {} as T);

            writeGenerationRef.current += 1;
            setParams(defaultParams);
            setTimeout(() => {
                updateUrl(defaultParams, replace);
            }, 0);
            onParamsChangeRef.current?.(defaultParams);
        },
        [updateUrl],
    );

    useEffect(() => {
        if (!isInRouterContext) return;
        if (searchParams.toString() === '') {
            const currentParams = parseUrlParams<T>(searchParams, configRef.current, initialValuesRef.current);

            const hasDefaults = Object.entries(configRef.current).some(
                ([key, paramConfig]) => paramConfig.defaultValue !== undefined && currentParams[key] !== undefined,
            );

            if (hasDefaults) {
                updateUrl(currentParams, true, true);
            }
        }
    }, [searchParams, updateUrl, isInRouterContext]);

    useEffect(() => {
        if (!isInRouterContext) return;
        isUpdatingFromUrl.current = true;

        const newParams = parseUrlParams<T>(searchParams, configRef.current, initialValuesRef.current);
        setParams(newParams);
        onParamsChangeRef.current?.(newParams);

        isUpdatingFromUrl.current = false;
    }, [searchParams, isInRouterContext]);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    if (!isInRouterContext) {
        return {
            params: {} as T,
            updateParams: () => {},
            resetParams: () => {},
            setParam: () => {},
            getParam: () => undefined,
        };
    }

    return {
        params: memoizedParams,
        updateParams,
        resetParams,
        setParam: (key: keyof T, value: unknown, options?: { replace?: boolean; immediate?: boolean }) => {
            updateParams({ [key]: value } as Partial<T>, options);
        },
        getParam: (key: keyof T) => memoizedParams[key],
    };
};

// Utility type for inferring parameter types from config
export type InferParamsType<T extends QueryParamConfigMap> = {
    [K in keyof T]: T[K]['type'] extends 'string'
        ? string
        : T[K]['type'] extends 'number'
          ? number
          : T[K]['type'] extends 'boolean'
            ? boolean
            : T[K]['type'] extends 'array'
              ? unknown[]
              : unknown;
};
